import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Input,
  Select,
  Space,
  message,
  Modal,
  Form,
  Tag,
  Tooltip,
  DatePicker,
  InputNumber,
  Image,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { PartnerLabel } from "../../components/common/DeletedTag";
import {
  listPendingDeposits,
  listApprovedDeposits,
  listRejectedDeposits,
  approveDeposit,
  rejectDeposit,
  bulkApproveDeposits,
  exportDeposits,
  downloadDepositReceipt,
  downloadDepositScreenshot,
} from "../../services/cashCod.service";

const { RangePicker } = DatePicker;

const STATUS_COLORS = {
  pending: { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  approved: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
};

const MODE_OPTIONS = [
  { label: "CDM", value: "CDM" },
  { label: "Mobile Transfer", value: "mobileTransfer" },
  { label: "Online Paid", value: "onlinePaid" },
];

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        textTransform: "capitalize",
        fontWeight: 600,
      }}
    >
      {status}
    </Tag>
  );
}

function formatDate(value) {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD");
}

function formatOmr(value) {
  return Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function openBlobPreview(fetcher, fallbackName) {
  const raw = await fetcher();
  const blob =
    raw instanceof Blob ? raw : new Blob([raw], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const isImage = (blob.type || "").startsWith("image/");
  if (isImage) {
    Modal.info({
      title: fallbackName,
      width: 520,
      content: <Image src={url} alt={fallbackName} style={{ maxWidth: "100%" }} />,
      afterClose: () => URL.revokeObjectURL(url),
    });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

/**
 * Shared Cash & COD deposit tab — Pending / Approved / Rejected
 */
export default function DepositTab({ status }) {
  const { canApprove } = usePermission("cashCOD");
  const { options: reasonOptions } = useSettingsDropdown("reasons", {
    filter: (item) =>
      !item.applicableModule ||
      item.applicableModule === "cashCOD" ||
      item.applicableModule === "all",
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [amountMin, setAmountMin] = useState(undefined);
  const [amountMax, setAmountMax] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [approveModal, setApproveModal] = useState({ open: false, record: null, bulk: false });
  const [rejectModal, setRejectModal] = useState({ open: false, record: null });
  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search || undefined,
        mode: mode || undefined,
        amountMin: amountMin ?? undefined,
        amountMax: amountMax ?? undefined,
      };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].startOf("day").toISOString();
        params.toDate = dateRange[1].endOf("day").toISOString();
      }

      const listFn =
        status === "pending"
          ? listPendingDeposits
          : status === "approved"
            ? listApprovedDeposits
            : listRejectedDeposits;

      const response = await listFn(params);
      setRows(response?.data?.records || []);
      setTotalCount(response?.data?.pagination?.total_count || 0);
      setSelectedRowKeys([]);
    } catch {
      message.error("Failed to load deposits");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, mode, dateRange, amountMin, amountMax, status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openApprove = (record) => {
    approveForm.resetFields();
    // Pre-fill the rider's submitted figures so the approver only edits what's wrong.
    approveForm.setFieldsValue({
      ordersCount: record.ordersCount ?? undefined,
      amountCollected: record.amountCollected ?? undefined,
      amount: record.amount,
    });
    setApproveModal({ open: true, record, bulk: false });
  };

  const openBulkApprove = () => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one deposit");
      return;
    }
    approveForm.resetFields();
    setApproveModal({ open: true, record: null, bulk: true });
  };

  const handleApproveSubmit = async () => {
    try {
      const values = await approveForm.validateFields();
      setActionLoading(true);
      if (approveModal.bulk) {
        const res = await bulkApproveDeposits(selectedRowKeys, {
          bankReferenceNo: values.bankReferenceNo,
        });
        message.success(res?.data?.msg || "Bulk approve completed");
      } else {
        await approveDeposit(approveModal.record._id, {
          bankReferenceNo: values.bankReferenceNo,
          ordersCount: values.ordersCount,
          amountCollected: values.amountCollected,
          amount: values.amount,
        });
        message.success("Deposit approved");
      }
      setApproveModal({ open: false, record: null, bulk: false });
      fetchList();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.errors?.[0]?.msg || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openReject = (record) => {
    rejectForm.resetFields();
    setRejectModal({ open: true, record });
  };

  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      setActionLoading(true);
      await rejectDeposit(rejectModal.record._id, {
        rejectionReason: values.rejectionReason,
      });
      message.success("Deposit rejected");
      setRejectModal({ open: false, record: null });
      fetchList();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.errors?.[0]?.msg || "Reject failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const raw = await exportDeposits({
        status,
        search: search || undefined,
        mode: mode || undefined,
        amountMin: amountMin ?? undefined,
        amountMax: amountMax ?? undefined,
        fromDate: dateRange?.[0]?.startOf("day").toISOString(),
        toDate: dateRange?.[1]?.endOf("day").toISOString(),
      });
      const blob =
        raw instanceof Blob
          ? raw
          : new Blob([raw], { type: "text/csv;charset=utf-8" });
      downloadBlob(
        blob,
        `Cash_Deposits_${status}_${dayjs().format("YYYY-MM-DD")}.csv`
      );
      message.success("Export downloaded");
    } catch {
      message.error("Export failed");
    }
  };

  const columns = [
    {
      title: "Partner ID",
      dataIndex: "partnerId",
      key: "partnerId",
      width: 130,
      render: (v, record) => (
        <span style={{ fontFamily: "monospace" }}>
          <PartnerLabel name={v || "—"} deleted={record.riderDeleted} />
        </span>
      ),
    },
    {
      title: "Partner Name",
      dataIndex: "partnerName",
      key: "partnerName",
      width: 180,
      render: (v, record) => (
        <strong>
          <PartnerLabel name={v} deleted={record.riderDeleted} />
        </strong>
      ),
    },
    {
      title: "Deposit Date",
      dataIndex: "depositDate",
      key: "depositDate",
      width: 120,
      render: formatDate,
    },
    {
      title: "Mode",
      dataIndex: "modeLabel",
      key: "modeLabel",
      width: 130,
    },
    {
      title: "Orders",
      dataIndex: "ordersCount",
      key: "ordersCount",
      width: 90,
      render: (v) => (v == null ? "—" : v),
    },
    {
      title: "Amount Collected (OMR)",
      dataIndex: "amountCollected",
      key: "amountCollected",
      width: 130,
      render: (v) => (v == null ? "—" : formatOmr(v)),
    },
    {
      title: "Amount Deposited (OMR)",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      render: formatOmr,
    },
    {
      title: "Receipt",
      key: "receipt",
      width: 90,
      render: (_, record) =>
        record.hasReceipt ? (
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            onClick={() =>
              openBlobPreview(
                () => downloadDepositReceipt(record._id),
                "Receipt"
              ).catch(() => message.error("Failed to open receipt"))
            }
          >
            View
          </Button>
        ) : (
          "—"
        ),
    },
    {
      title: "App Screenshot",
      key: "screenshot",
      width: 120,
      render: (_, record) =>
        record.hasAppScreenshot ? (
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            onClick={() =>
              openBlobPreview(
                () => downloadDepositScreenshot(record._id),
                "App Screenshot"
              ).catch(() => message.error("Failed to open screenshot"))
            }
          >
            View
          </Button>
        ) : (
          "—"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <StatusBadge status={s} />,
    },
  ];

  if (status === "pending" || status === "approved") {
    columns.splice(9, 0, {
      title: "Bank Ref",
      dataIndex: "bankReferenceNo",
      key: "bankReferenceNo",
      width: 140,
      render: (v) => v || "—",
    });
  }

  if (status === "approved") {
    columns.push(
      {
        title: "Approved By",
        dataIndex: "approvedByName",
        key: "approvedByName",
        width: 140,
      },
      {
        title: "Approval Date",
        dataIndex: "actionDate",
        key: "actionDate",
        width: 120,
        render: formatDate,
      }
    );
  }

  if (status === "rejected") {
    columns.push(
      {
        title: "Rejected By",
        dataIndex: "rejectedByName",
        key: "rejectedByName",
        width: 140,
      },
      {
        title: "Rejection Date",
        dataIndex: "actionDate",
        key: "actionDate",
        width: 120,
        render: formatDate,
      },
      {
        title: "Rejection Reason",
        dataIndex: "rejectionReasonLabel",
        key: "rejectionReasonLabel",
        width: 180,
        render: (v) => (
          <span style={{ color: "#b91c1c", fontWeight: 500 }}>{v || "—"}</span>
        ),
      }
    );
  }

  if (status === "pending") {
    columns.push({
      title: "Action",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) =>
        canApprove ? (
          <Space size="small">
            <Tooltip title="Approve">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => openApprove(record)}
                style={{ background: "#111827" }}
              />
            </Tooltip>
            <Tooltip title="Reject">
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => openReject(record)}
              />
            </Tooltip>
          </Space>
        ) : (
          "—"
        ),
    });
  }

  return (
    <div>
      <div
        style={{
          background: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: 10,
          padding: 16,
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 220px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Search Partner
          </div>
          <Input.Search
            placeholder="Partner ID or Name"
            allowClear
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
          />
        </div>

        <div style={{ width: 160 }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Mode
          </div>
          <Select
            allowClear
            placeholder="All Modes"
            style={{ width: "100%" }}
            options={MODE_OPTIONS}
            value={mode}
            onChange={(v) => {
              setPage(1);
              setMode(v);
            }}
          />
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Date Range
          </div>
          <RangePicker
            value={dateRange}
            onChange={(v) => {
              setPage(1);
              setDateRange(v);
            }}
          />
        </div>

        <div style={{ width: 110 }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Min Deposited
          </div>
          <InputNumber
            style={{ width: "100%" }}
            step={0.001}
            value={amountMin}
            onChange={(v) => {
              setPage(1);
              setAmountMin(v);
            }}
          />
        </div>
        <div style={{ width: 110 }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Max Deposited
          </div>
          <InputNumber
            style={{ width: "100%" }}
            step={0.001}
            value={amountMax}
            onChange={(v) => {
              setPage(1);
              setAmountMax(v);
            }}
          />
        </div>

        <Button icon={<ReloadOutlined />} onClick={fetchList}>
          Refresh
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
        {status === "pending" && canApprove && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            disabled={!selectedRowKeys.length}
            onClick={openBulkApprove}
            style={
              selectedRowKeys.length
                ? { background: "#111827", borderColor: "#111827" }
                : undefined
            }
          >
            Bulk Approve
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={(p) => setPage(p)}
        scroll={{ x: 1650 }}
        rowSelection={
          status === "pending" && canApprove
            ? {
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }
            : undefined
        }
      />

      <Modal
        title={approveModal.bulk ? "Bulk Approve Deposits" : "Approve Deposit"}
        open={approveModal.open}
        onCancel={() => setApproveModal({ open: false, record: null, bulk: false })}
        onOk={handleApproveSubmit}
        confirmLoading={actionLoading}
        okText="Approve"
        forceRender
      >
        {approveModal.bulk ? (
          <p style={{ marginBottom: 12, color: "#64748b" }}>
            Applied to {selectedRowKeys.length} selected deposit(s). Each keeps
            its submitted orders and amounts — approve individually to edit them.
          </p>
        ) : null}
        <Form form={approveForm} layout="vertical">
          {!approveModal.bulk && (
            <>
              <Form.Item
                name="ordersCount"
                label="Number of Orders"
                rules={[
                  { required: true, message: "Number of orders is required" },
                ]}
              >
                <InputNumber
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Enter number of orders"
                />
              </Form.Item>
              <Form.Item name="amountCollected" label="Amount Collected (OMR)">
                <InputNumber
                  step={0.001}
                  precision={3}
                  style={{ width: "100%" }}
                  placeholder="0.000"
                />
              </Form.Item>
              <Form.Item
                name="amount"
                label="Amount Deposited (OMR)"
                rules={[
                  { required: true, message: "Amount deposited is required" },
                ]}
              >
                <InputNumber
                  step={0.001}
                  precision={3}
                  style={{ width: "100%" }}
                  placeholder="0.000"
                />
              </Form.Item>
            </>
          )}
          <Form.Item name="bankReferenceNo" label="Bank Reference No. (optional)">
            <Input placeholder="Enter bank reference number" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject Deposit"
        open={rejectModal.open}
        onCancel={() => setRejectModal({ open: false, record: null })}
        onOk={handleRejectSubmit}
        confirmLoading={actionLoading}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, message: "Select a rejection reason" }]}
          >
            <Select
              placeholder="Select reason"
              options={reasonOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
